import { apiFetch, tokenStore } from "./client";

export type UserRole = "admin" | "employee";

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  // Présent uniquement après fetchProfile (pas dans la réponse minimale du
  // login). Pour les admins : []. Pour les employees : liste des phone_ids
  // assignés. Le frontend l'utilise pour adapter l'UI (cacher des liens,
  // filtrer des lists, etc.).
  allowed_phone_ids?: string[];
}

export interface AssignedPhone {
  id: string;
  name: string;
  status?: string;
}

export interface FullProfile extends PublicUser {
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  allowed_phone_ids: string[];
  allowed_phones: AssignedPhone[];
}

interface LoginResponse {
  success: boolean;
  token: string;
  user: PublicUser;
  error?: string;
}

interface ProfileResponse {
  success: boolean;
  user: FullProfile;
  error?: string;
}

export async function login(email: string, password: string): Promise<FullProfile> {
  const data = await apiFetch<LoginResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (!data.success || !data.token) {
    throw new Error(data.error ?? "Login échoué");
  }
  tokenStore.set(data.token);
  // On récupère le profil complet (avec allowed_phone_ids) immédiatement.
  return await fetchProfile();
}

// Utilisé au démarrage de l'app pour restaurer la session si un token est
// déjà en localStorage. Retourne le profil complet (rôle + tels assignés).
export async function fetchMe(): Promise<FullProfile> {
  return await fetchProfile();
}

export async function fetchProfile(): Promise<FullProfile> {
  const data = await apiFetch<ProfileResponse>("/api/auth/profile");
  if (!data.success) {
    throw new Error(data.error ?? "Profil indisponible");
  }
  return data.user;
}

export async function logout(): Promise<void> {
  try {
    await apiFetch("/api/auth/logout", { method: "POST" });
  } finally {
    tokenStore.clear();
  }
}

export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await apiFetch("/api/auth/me/password", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}
